import React, { useEffect, useRef, useState } from 'react';
import { Clock3, ExternalLink, RefreshCw } from 'lucide-react';
import SlidePanel from '../SlidePanel';
import { apiService } from '../../services/api';
import { statusStyle } from '../../utils/reportFormat';
import Disclosure from './Disclosure';
import ItemList from './ItemList';
import Journey from './Journey';
import PeopleList from './PeopleList';
import ProgressBlock from './ProgressBlock';
import { EpicSummary, EpicTarget, StatusGroup } from './types';
import * as fmt from '../../lib/format';
import { scrollBehavior } from '../../utils/motion';

const JIRA_BROWSE = 'https://saral-tracker.atlassian.net/browse/';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; data: EpicSummary }
  | { status: 'missing' }
  | { status: 'error'; message: string };

function pluralType(name: string, n: number): string {
  if (n === 1) return name;
  if (/y$/i.test(name)) return name.replace(/y$/i, 'ies');
  if (/s$/i.test(name)) return name;
  return `${name}s`;
}

function Header({ target, data }: { target: EpicTarget; data: EpicSummary | null }) {
  const statusLabel = data ? data.epic.status_at_period_end ?? data.epic.status_now : null;
  return (
    <header>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <a
            href={`${JIRA_BROWSE}${target.issueKey}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200/60 transition-colors hover:bg-blue-100"
          >
            {target.issueKey}
            <ExternalLink className="h-3 w-3 opacity-60" aria-hidden />
          </a>
          <span className="truncate text-sm text-gray-500">{target.projectName}</span>
        </div>
        <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
          {target.periodLabel}
        </span>
      </div>
      <h2 className="mt-3 text-xl font-semibold leading-snug tracking-tight text-gray-900">
        {target.title || data?.epic.name}
      </h2>
      {data && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {statusLabel && (
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${statusStyle(statusLabel).badge}`}>
              {statusLabel}
            </span>
          )}
          {!data.period.is_current && <span className="text-xs text-gray-500">at the end of the period</span>}
          {data.epic.created_on && <span className="text-xs text-gray-500">Created {fmt.date(data.epic.created_on)}</span>}
        </div>
      )}
      {data?.epic.description && (
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-gray-600">{data.epic.description}</p>
      )}
    </header>
  );
}

function Narrative({ narrative }: { narrative: EpicSummary['narrative'] }) {
  const [headline, ...rest] = narrative.lines;
  return (
    <section aria-label="Summary" className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
      <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">The short version</h3>
      {narrative.context && (
        <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-blue-700">
          <Clock3 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {narrative.context}
        </p>
      )}
      <p className="mt-2 text-lg font-semibold leading-snug tracking-tight text-gray-900">{headline}</p>
      {rest.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-[0.9375rem] leading-relaxed text-gray-600">
          {rest.map((line, i) => (
            <li key={`${i}-${line}`}>{line}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Tile({ value, label, sub }: { value: string; label: string; sub?: string }) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
      <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-[0.6875rem] tabular-nums text-gray-500">{sub}</div>}
    </div>
  );
}

function Skeleton() {
  const bar = (w: string, h = 'h-3') => (
    <div className={`${h} ${w} animate-pulse rounded bg-gray-100 motion-reduce:animate-none`} />
  );
  return (
    <div className="space-y-5" role="status" aria-label="Loading summary">
      <div className="space-y-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
        {bar('w-24')}
        {bar('w-4/5', 'h-5')}
        {bar('w-3/5')}
        {bar('w-2/3')}
      </div>
      <div className="space-y-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
        {bar('w-20')}
        {bar('w-full', 'h-2.5')}
        <div className="flex gap-2">
          {bar('w-20', 'h-7')}
          {bar('w-24', 'h-7')}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 2xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="space-y-2 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
            {bar('w-10', 'h-6')}
            {bar('w-14')}
          </div>
        ))}
      </div>
    </div>
  );
}

function Body({ data }: { data: EpicSummary }) {
  const [filter, setFilter] = useState<StatusGroup | null>(null);
  const [open, setOpen] = useState({ inside: false, people: false, journey: false });
  const insideRef = useRef<HTMLElement>(null);
  const toggle = (k: keyof typeof open) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  // Picking a status in the progress block opens the list and brings it into view.
  const pickStatus = (g: StatusGroup | null) => {
    setFilter(g);
    if (!g) return;
    setOpen((o) => ({ ...o, inside: true }));
    requestAnimationFrame(() => {
      insideRef.current?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    });
  };

  const { counts, hours, items, people, timeline, epic, period } = data;
  const ticketTypes = counts.by_type.filter((t) => !t.is_subtask);

  return (
    <div className="space-y-5">
      <Narrative narrative={data.narrative} />
      <ProgressBlock counts={counts.tickets} filter={filter} onFilter={pickStatus} />

      <div>
        <div className="grid grid-cols-2 gap-3 2xl:grid-cols-4">
          <Tile
            value={fmt.count(counts.tickets.total)}
            label="Tickets"
            sub={counts.tickets.by_group.invalid > 0 ? `${counts.tickets.by_group.invalid} closed as invalid` : undefined}
          />
          <Tile value={fmt.count(counts.subtasks.total)} label="Sub-tasks" />
          <Tile
            value={fmt.hours(hours.period)}
            label="Hours"
            sub={hours.to_date - hours.period >= 0.05 ? `${fmt.hours(hours.to_date)} to date` : undefined}
          />
          <Tile value={String(hours.people)} label="People" sub="logged time" />
        </div>
        {ticketTypes.length > 0 && (
          <p className="mt-2.5 px-1 text-xs leading-relaxed text-gray-500">
            {ticketTypes.map((t) => `${t.count} ${pluralType(t.issue_type, t.count)}`).join(' · ')}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <Disclosure
          title="What's inside"
          count={items.length}
          hint={`${fmt.counted(counts.tickets.total, 'ticket')} · ${fmt.counted(counts.subtasks.total, 'sub-task')}`}
          open={open.inside}
          onToggle={() => toggle('inside')}
          sectionRef={insideRef}
        >
          <ItemList items={items} filter={filter} onClearFilter={() => setFilter(null)} />
        </Disclosure>
        <Disclosure
          title="Who worked on it"
          count={people.length}
          hint={`${fmt.hours(hours.period)} this period`}
          open={open.people}
          onToggle={() => toggle('people')}
        >
          <PeopleList people={people} byLogType={hours.by_log_type} />
        </Disclosure>
        <Disclosure
          title="Epic journey"
          count={timeline.length}
          hint={period.is_current ? epic.status_now : undefined}
          open={open.journey}
          onToggle={() => toggle('journey')}
        >
          <Journey
            timeline={timeline}
            statusNow={epic.status_now}
            statusAtEnd={epic.status_at_period_end}
            isCurrent={period.is_current}
          />
        </Disclosure>
      </div>
    </div>
  );
}

interface Props {
  isOpen: boolean;
  target: EpicTarget | null;
  onClose: () => void;
}

// The drill-down behind an Epic row on the Reports page. The header shows at once from the
// row itself; the summary fills in when the request returns.
export default function EpicSummaryPanel({ isOpen, target, onClose }: Props) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const issueKey = target?.issueKey;
  const periodKey = target && 'period' in target ? target.period : undefined;
  const from = target && 'from' in target ? target.from : undefined;
  const to = target && 'to' in target ? target.to : undefined;
  useEffect(() => {
    if (!isOpen || !issueKey) return;
    if (!periodKey && !(from && to)) return;
    let cancelled = false;
    setState({ status: 'loading' });
    apiService
      .getEpicSummary(issueKey, periodKey ? { period: periodKey } : { from: from as string, to: to as string })
      .then((res) => {
        if (cancelled) return;
        setState(
          res?.success ? { status: 'ready', data: res.data as EpicSummary } : { status: 'error', message: res?.error || 'Could not load the summary' }
        );
      })
      .catch((e: any) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setState({ status: 'missing' });
        else setState({ status: 'error', message: e?.response?.data?.error || e?.message || 'Could not load the summary' });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, issueKey, periodKey, from, to, attempt]);

  // Only show a summary that belongs to the epic and period on screen. Right after a row is
  // opened the previous epic's data can still be in state for one render.
  const data =
    state.status === 'ready' &&
    state.data.epic.issue_key === issueKey &&
    (periodKey ? state.data.period.key === periodKey : state.data.period.from === from && state.data.period.to === to)
      ? state.data
      : null;
  const loading = state.status === 'loading' || (state.status === 'ready' && data === null);

  // Keyboard users: move focus into the panel when it opens and hand it back to the row that
  // opened it when it closes. (The panel is rendered at the end of the page, so without this
  // the Tab key would have to cross the whole report to reach it.)
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    contentRef.current?.focus({ preventScroll: true });
    return () => returnFocusTo.current?.focus?.({ preventScroll: true });
  }, [isOpen]);

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="Epic summary" widthPercent={42} minWidthPx={480}>
      {target && (
        <div ref={contentRef} tabIndex={-1} className="mx-auto max-w-2xl space-y-5 pb-10 outline-none">
          <Header target={target} data={data} />
          {loading && <Skeleton />}
          {data && <Body key={`${data.epic.issue_key}|${data.period.from}|${data.period.to}`} data={data} />}
          {state.status === 'missing' && (
            <div className="rounded-2xl bg-white px-5 py-10 text-center shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
              <p className="text-sm font-medium text-gray-900">This epic is no longer available.</p>
              <p className="mt-1 text-sm text-gray-500">It may have been deleted in Jira since the report was built.</p>
            </div>
          )}
          {state.status === 'error' && (
            <div className="rounded-2xl bg-white px-5 py-10 text-center shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400" role="alert">
              <p className="text-sm font-medium text-gray-900">The summary could not be loaded.</p>
              <p className="mt-1 text-sm text-gray-500">{state.message}</p>
              <button
                type="button"
                onClick={() => setAttempt((n) => n + 1)}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-[background-color,transform] duration-150 hover:bg-blue-700 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                <RefreshCw className="h-4 w-4" aria-hidden />
                Try again
              </button>
            </div>
          )}
        </div>
      )}
    </SlidePanel>
  );
}
